import React, { useMemo, useState } from "react";

import {
	faChevronLeft,
	faEllipsisH,
	faFontCase,
	faKeyboardDown,
	faLayerGroup,
	faPlus,
	faUndo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import cx from "classnames";
import { flatMap, memoize } from "lodash";
import { EditorView } from "prosemirror-view";

import type { MenuEntry, MenuExtension } from "~/lib/prose/extension";
import { groupEntries } from "~/lib/prose/plugins/toolbar";
import { useRecordingState } from "~/lib/videoRecording";
import { Button } from "~/ui";
import { LabelIconForButton } from "~/ui/button";
import IconButton from "~/ui/button/IconButton";
import { Menu } from "~/ui/menu";

import { isActive, isDisabled, ToolbarItem } from "./FloatingToolbar";

import styles from "./PinnedToolbar.module.css";

function VerticalRule() {
	return <span className={styles.verticalRule} />;
}

function MobileBackButton({ onClick }: { onClick: () => void }) {
	return <IconButton size="large" variant="tertiary" icon={faChevronLeft} onClick={onClick} />;
}

function BlockTypeList({
	sections,
	addBlock: _addBlock,
}: {
	sections: ReturnType<typeof groupEntries>;
	addBlock: (entry: MenuEntry) => void;
}) {
	const addBlock = React.useMemo(() => memoize((entry: MenuEntry) => () => _addBlock(entry)), [_addBlock]);
	const isRecording = useRecordingState();

	return (
		<>
			{sections.types.map(([name, entries], i) => {
				const filteredEntries = entries
					.filter(e => {
						return e.showInToolbar !== false;
					})
					.map(entry => {
						return (
							entry.icon &&
							entry.title && (
								<IconButton
									size="large"
									variant="tertiary"
									onClick={addBlock(entry)}
									icon={entry.icon}
									disabled={entry.title === "Custom video" && isRecording}
									style={{ opacity: isRecording && entry.title === "Custom video" ? 0.5 : 1 }}
								/>
							)
						);
					});

				if (filteredEntries.length === 0) return;

				return (
					<React.Fragment key={name}>
						{i > 0 && <VerticalRule />}
						{filteredEntries}
					</React.Fragment>
				);
			})}
		</>
	);
}

interface FormattingToolbarProps {
	editorView: EditorView;
	extension: MenuExtension;
	entries: readonly MenuEntry[];
	className?: string;
}

function FormattingToolbar({ entries, extension, editorView, className }: FormattingToolbarProps) {
	const { selection, doc } = editorView.state;

	let filteredEntries = entries;

	filteredEntries = entries.filter(entry => entry.group !== "insert");

	const sections = groupEntries(filteredEntries);
	const currentBlockTypeEntry = flatMap(sections.types, ([, entries]) => entries).find(
		e => e.active?.(editorView.state) ?? isActive(e, extension, editorView.state),
	);

	let hasLinkedBlockInSelection = false;
	let hasImageStripInSelection =
		selection.$from.node(1)?.type.name === "image_strip" || selection.$to.node(1)?.type.name === "image_strip";
	doc.slice(selection.from, selection.to).content.descendants(node => {
		if (!hasImageStripInSelection && node.type.name.startsWith("image_strip")) {
			hasImageStripInSelection = true;
		}
		if (!hasLinkedBlockInSelection && node.type.name === "todo" && node.attrs.originalBlockId)
			hasLinkedBlockInSelection = true;
		return false;
	});

	const renderItems = React.useCallback(
		(group: [string, MenuEntry[]][], isRadixMenu: boolean) => {
			return group.map(([name, entries]) =>
				entries.map(e => (
					<ToolbarItem
						key={name + e.title}
						asMenuItem={isRadixMenu}
						isMobile
						entry={e}
						extension={extension}
						editorView={editorView}
						active={isActive(e, extension, editorView.state)}
						disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, editorView)}
					/>
				)),
			);
		},
		[extension, editorView],
	);

	const renderGroup = React.useCallback(
		(group: [string, MenuEntry[]][], includeSeparator = true) => {
			if (!group || group.length === 0) return;

			return (
				<>
					<div className={styles.iconGroup}>{renderItems(group, false)}</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems],
	);

	const renderIconGroup = React.useCallback(
		(
			group: [string, MenuEntry[]][],
			numberOfItemsToShowInBar?: number,
			icon?: IconDefinition,
			includeSeparator = true,
		) => {
			if (!group || group.length === 0) return;

			// If numberOfItemsToShowInBar is specified, we render the first n items in the bar, and the rest in a menu
			const itemsToShow = group[0][1].splice(0, numberOfItemsToShowInBar ?? 0);
			return (
				<>
					<div className={cx(styles.iconGroup)}>
						{!!numberOfItemsToShowInBar && renderItems([[group[0][0], itemsToShow]], false)}
						<Menu mobileHeaderTitle="Extra formatting">
							<Menu.Trigger asChild>
								{icon ? (
									<Button size="slim" variant="tertiary" className={styles.groupMenu}>
										<LabelIconForButton icon={icon} fixedWidth side="left" />
										<LabelIconForButton icon={faCaretDown} side="right" className={styles.caret} />
									</Button>
								) : (
									<IconButton size="large" variant="tertiary" icon={faEllipsisH} />
								)}
							</Menu.Trigger>
							<Menu.Content>
								<Menu.Item>{renderItems(group, true)}</Menu.Item>
							</Menu.Content>
						</Menu>
					</div>
					{includeSeparator && <VerticalRule />}
				</>
			);
		},
		[renderItems],
	);

	const {
		mostUsedAndSocial,
		formatting,
		colouring,
		indent,
		//reaction
	} = sections;

	return (
		<div className={className}>
			{!hasLinkedBlockInSelection && sections.types.reduce((n, [, { length }]) => n + length, 0) > 1 && (
				<>
					<div className={styles.iconGroup}>
						<Menu mobileHeaderTitle="Change block type">
							<Menu.Trigger disabled={hasLinkedBlockInSelection || hasImageStripInSelection} asChild>
								<Button size="slim" variant="tertiary" className={styles.groupMenu}>
									{currentBlockTypeEntry?.icon ? (
										<LabelIconForButton side="left" icon={currentBlockTypeEntry.icon} fixedWidth />
									) : (
										<LabelIconForButton side="left" icon={faLayerGroup} fixedWidth />
									)}
									<LabelIconForButton side="right" icon={faCaretDown} className={styles.caret} />
								</Button>
							</Menu.Trigger>
							<Menu.Content>
								<Menu.Item>
									{sections.types.map(([name, entries]) => (
										<React.Fragment key={name}>
											{entries.map(e => (
												<ToolbarItem
													key={e.title}
													asMenuItem
													isMobile
													entry={e}
													extension={extension}
													editorView={editorView}
													active={isActive(e, extension, editorView.state)}
													disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, editorView)}
												/>
											))}
										</React.Fragment>
									))}
								</Menu.Item>
							</Menu.Content>
						</Menu>
					</div>
					<VerticalRule />
				</>
			)}
			{/* TODO: For some reason reactions is bugging out… */}
			{/* I'm getting the error "Can't render reactions menu without reactions extension" despite it being there. */}
			{/* {renderGroup([...(mostUsedAndSocial || []), ...(reaction || [])])} */}
			{renderGroup(mostUsedAndSocial)}
			{renderIconGroup(formatting, 2)}
			{renderGroup(colouring)}
			{renderGroup(indent, false)}
		</div>
	);
}

interface PinnedToolbarProps {
	closeKeyboard: () => void;
	entries: readonly MenuEntry[];
	addBlock: (e: MenuEntry) => void;
	editorView: EditorView;
	extension: MenuExtension;
	undo: () => void;
}

export default function PinnedToolbar({
	extension,
	editorView,
	closeKeyboard,
	entries,
	addBlock,
	undo,
}: PinnedToolbarProps) {
	type ToolbarState = "home" | "add" | "format";
	const [state, _setState] = useState<ToolbarState>("home");
	const makeSetState = useMemo(
		() =>
			memoize((name: ToolbarState) => () => {
				editorView.focus();
				_setState(name);
			}),
		[editorView, _setState],
	);

	switch (state) {
		case "home": {
			return (
				<div className={cx(styles.toolbar, styles.homeToolbar)}>
					<div className={styles.iconGroup}>
						<IconButton size="large" variant="tertiary" icon={faPlus} onClick={makeSetState("add")} />
						<IconButton size="large" variant="tertiary" icon={faFontCase} onClick={makeSetState("format")} />
						<IconButton size="large" variant="tertiary" icon={faUndo} onClick={undo} />
					</div>
					<div className={styles.iconGroup}>
						<IconButton size="large" variant="tertiary" icon={faKeyboardDown} onClick={closeKeyboard} />
					</div>
				</div>
			);
		}
		case "add": {
			return (
				<div className={styles.toolbar}>
					<div className={styles.iconGroup}>
						<MobileBackButton onClick={makeSetState("home")} />
					</div>
					<div className={cx(styles.iconGroup, styles.scrollingGroup)}>
						<BlockTypeList sections={groupEntries(entries)} addBlock={addBlock} />
					</div>
				</div>
			);
		}
		case "format": {
			return (
				<div className={styles.toolbar}>
					<div className={styles.iconGroup}>
						<MobileBackButton onClick={makeSetState("home")} />
					</div>
					<FormattingToolbar
						entries={entries}
						extension={extension}
						editorView={editorView}
						className={cx(styles.iconGroup, styles.scrollingGroup)}
					/>
				</div>
			);
		}
	}
}
